import type { ReviewChangelogEntry } from './knowledge-hub-utils'

export interface DocumentEditRow {
  id: string
  document_id: string
  timestamp: string
  changed_by: 'user' | 'ai' | 'system'
  ai_conversation_id: string | null
  /**
   * Authenticated user whose session was active at write time (migration 084, broadened
   * by 085). Populated for 'user' edits, and for 'ai'/'system' edits made through a
   * logged-in session (e.g. an AI chat edit, or a Knowledge Hub action by a known
   * account). Null for pre-migration rows, background/automated edits with no human
   * session, and unauthenticated local dev saves.
   */
  user_id?: string | null
  /** COALESCE(users.full_name, users.email) for user_id, joined server-side in GET /:id/history. */
  user_name?: string | null
  metadata: {
    event_type?: string
    event_reason?: string
    /** Raw Knowledge Hub actor identifier (email or flow marker) when user_id didn't resolve to an account - see resolveActor() in knowledge-hub-service.js. */
    actor?: string
    /** What triggered this save: 'manual' (Cmd+S) | 'autosave' (blur/tab-switch) | 'restore' | 'undo' | 'delete'. See formatEditSourceLabel(). */
    saveTrigger?: string
    /** Set on chat-stream apply-edit/reject-edit rows. */
    source?: string
    /** Write surface: webapp-chat | mcp | vsix-sync | webapp-editor | api */
    channel?: string
    /** Client surface when known: webapp | cursor | claude-code | other */
    platform?: string
    /** MCP clientInfo.name when available */
    client?: string
    /** Model id when known (webapp chat; rarely MCP) */
    model?: string
    /** Legacy alias for channel */
    via?: string
    /** Skill slug that produced this edit (audit field) */
    skill_slug?: string
    /** Opaque session/run identifier (audit field) */
    session_id?: string
    /** One-line reason for this change (audit field) */
    reason?: string
    /** Agent profile name, not model name (audit field) */
    agent_name?: string
    /** Agent type token (audit field) */
    agent_kind?: string
    [key: string]: unknown
  } | null
  change_stats: { added_lines?: number; added_chars?: number; change_type?: string } | null
  previous_length: number
  new_length: number
  content_before?: string
  content_after?: string
}

/** Named checkpoint / Knowledge Hub fence fields (shared by version parents for badge coloring). */
export type TimelineMilestoneFields = {
  /** Stable row id: the document_edits / version row id, or a synthetic `fence-*` / `session-*` id. */
  id: string
  timestamp: string
  label: string
  reason?: string
  /** Present when backed by a document_edits / version snapshot row (diff via editHasDiffContent). */
  edit?: DocumentEditRow
  /** Attribution for fence-only milestones (document_edits rows use `edit.changed_by` instead). */
  changedBy?: string
  /** Raw `review-changelog` fence action value, when this milestone came from Knowledge Hub. */
  actionSlug?: string
}

/** @deprecated Use TimelineVersionParent - kept as alias for badge helper callers. */
export type TimelineMilestoneItem = TimelineMilestoneFields & { kind?: 'milestone' }

/**
 * Version / session parent in a day group. Granular edits nest as children
 * (GDocs-aligned: expand under named version or auto session).
 */
export type TimelineVersionParent = TimelineMilestoneFields & {
  kind: 'version'
  /** Named checkpoint / document_versions row, or auto session of orphan edits. */
  variant: 'named' | 'session'
  children: DocumentEditRow[]
}

export type TimelineDayItem = TimelineVersionParent

/** One calendar day of version/session parents (newest -> oldest within the day). */
export type TimelineRow = {
  kind: 'day-group'
  dateKey: string
  dateLabel: string
  items: TimelineVersionParent[]
}

/**
 * Same local-minute cluster of version parents. Collapses noisy KH verify / AI edit /
 * review-requested bursts that share a clock minute into one expandable group.
 */
export type TimelineTimeCluster = {
  id: string
  /** Local calendar minute key (year-month-day-hour-minute). */
  timeKey: string
  timeLabel: string
  /** Newest item timestamp in the cluster (for titles / tooltips). */
  timestamp: string
  items: TimelineVersionParent[]
}

/** Local minute bucket for clustering sibling approval/edit rows. */
export function minuteBucketKey(timestamp: string): string {
  const d = new Date(timestamp)
  return [
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
  ].join('-')
}

/** Localized clock time for cluster headers (hour + minute). */
export function formatClusterTimeLabel(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Group already-sorted (newest -> oldest) day items into same-minute clusters.
 * Sequential scan preserves order; non-contiguous same minutes stay separate.
 */
export function clusterTimelineItemsByMinute(
  items: TimelineVersionParent[]
): TimelineTimeCluster[] {
  const clusters: TimelineTimeCluster[] = []
  for (const item of items) {
    const timeKey = minuteBucketKey(item.timestamp)
    const last = clusters[clusters.length - 1]
    if (last && last.timeKey === timeKey) {
      last.items.push(item)
      continue
    }
    clusters.push({
      id: `time-${timeKey}-${item.id}`,
      timeKey,
      timeLabel: formatClusterTimeLabel(item.timestamp),
      timestamp: item.timestamp,
      items: [item],
    })
  }
  return clusters
}

/** Distinct badge labels for a collapsed cluster summary (newest first, capped). */
export function clusterSummaryBadges(
  items: TimelineVersionParent[],
  limit = 3
): string[] {
  const seen = new Set<string>()
  const labels: string[] = []
  for (const item of items) {
    const label = item.label?.trim()
    if (!label || seen.has(label)) continue
    seen.add(label)
    labels.push(label)
    if (labels.length >= limit) break
  }
  return labels
}

/** First non-empty reason/note in a cluster (newest first). */
export function clusterLatestNote(items: TimelineVersionParent[]): string | undefined {
  for (const item of items) {
    const note = item.reason?.trim()
    if (note) return note
  }
  return undefined
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  created: 'Created',
  verify: 'Verified',
  publish: 'Published',
  approve: 'Approved',
  'review-requested': 'Review requested',
  'major-revision': 'Major revision',
  'save-version': 'Named version',
  archived: 'Archived',
  unarchived: 'Unarchived',
}

/** Returns a human-readable label for a checkpoint event_type value. */
export function formatEventType(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] ?? eventType.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Badge color classes for `document_edits`-backed checkpoint milestones (event_type),
 * mapped to the same palette as FENCE_ACTION_BADGE_COLORS below so a "Verified" milestone
 * looks identical whether it came from a document_edits checkpoint or a Knowledge Hub
 * review-changelog fence entry - previously these rendered with different (generic vs
 * colored) badge styling for conceptually the same action.
 */
export const EVENT_TYPE_BADGE_COLORS: Record<string, string> = {
  created: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  verify: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  approve: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  publish: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'review-requested': 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'major-revision': 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  archived: 'bg-muted text-muted-foreground border-border',
  unarchived: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
}

/**
 * Human-readable labels for `review-changelog` fence `action` values (Knowledge Hub).
 * Copy matches the approved prototype (`test-knowledge-hub-changelog.tsx`).
 */
const FENCE_ACTION_LABELS: Record<string, string> = {
  'review-requested': 'Review requested',
  'ai-updated': 'Updated by AI',
  'imported-from-guru': 'Imported from Guru',
  expired: 'Review expired',
  verified: 'Verified',
  archived: 'Archived',
}

/** Badge color classes per fence action, matching the approved prototype's ACTION_COLORS. */
export const FENCE_ACTION_BADGE_COLORS: Record<string, string> = {
  verified: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  'review-requested': 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'ai-updated': 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  'imported-from-guru': 'bg-muted text-muted-foreground border-border',
  expired: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  archived: 'bg-muted text-muted-foreground border-border',
}

/** Returns a human-readable label for a `review-changelog` fence action value. */
export function formatFenceAction(action: string): string {
  return FENCE_ACTION_LABELS[action] ?? action.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Fence actions that Knowledge Hub *normally* already writes as a `document_edits`
 * checkpoint (see `writeCheckpoint` in `knowledge-hub-service.js`, called from
 * `markVerified`). A fence entry with one of these actions is only dropped from the
 * merge when a same-event `document_edits` milestone is actually found nearby in time
 * (see `VERIFY_DEDUPE_WINDOW_MS`) - otherwise it's kept as a fence-only milestone so
 * hand-authored or legacy fence entries without a backing checkpoint are never
 * silently lost from the timeline.
 */
const FENCE_ACTIONS_COVERED_BY_DOCUMENT_EDITS = new Set(['verified'])

/**
 * Max time delta between a fence entry's `at` and a `document_edits` checkpoint's
 * `timestamp` for them to be treated as the same real-world event. Both are written
 * within the same request in `knowledge-hub-service.js` (checkpoint insert, then the
 * fence append a few lines later), so this is generous on purpose.
 */
const VERIFY_DEDUPE_WINDOW_MS = 10 * 60 * 1000

type TimelineEvent =
  | { type: 'edit'; timestamp: string; edit: DocumentEditRow }
  | { type: 'fence'; timestamp: string; id: string; label: string; reason?: string; changedBy: string; actionSlug: string }

type FlatDayItem =
  | { kind: 'edit'; edit: DocumentEditRow }
  | { kind: 'milestone'; fields: TimelineMilestoneFields }

/**
 * Newest -> oldest flat items for one day: buffered granular edits become children
 * of the next older named milestone; leftover edits become a session parent.
 */
function nestFlatDayItems(flat: FlatDayItem[]): TimelineVersionParent[] {
  const parents: TimelineVersionParent[] = []
  let pendingEdits: DocumentEditRow[] = []

  for (const item of flat) {
    if (item.kind === 'edit') {
      pendingEdits.push(item.edit)
      continue
    }
    parents.push({
      kind: 'version',
      variant: 'named',
      ...item.fields,
      children: pendingEdits,
    })
    pendingEdits = []
  }

  if (pendingEdits.length > 0) {
    const tip = pendingEdits[0]
    // Single orphan edit: one parent row, no nest. Multiple: session header + all edits as children.
    const multi = pendingEdits.length > 1
    parents.push({
      kind: 'version',
      variant: 'session',
      id: multi ? `session-${tip.id}` : tip.id,
      timestamp: tip.timestamp,
      label: multi ? 'Session' : formatEditSourceLabel(tip) || 'Edit',
      edit: tip,
      reason: multi ? `${pendingEdits.length} edits` : undefined,
      children: multi ? pendingEdits : [],
    })
  }

  return parents
}

/** Single-pass grouping shared by buildVersionTimeline and the changelog-merged variant. Events must be sorted newest -> oldest. */
function groupTimelineEvents(events: TimelineEvent[]): TimelineRow[] {
  const rows: TimelineRow[] = []
  let currentDayKey: string | null = null
  let currentFlat: FlatDayItem[] = []

  const flushGroup = () => {
    if (currentFlat.length > 0 && currentDayKey) {
      const firstTs =
        currentFlat[0].kind === 'edit'
          ? currentFlat[0].edit.timestamp
          : currentFlat[0].fields.timestamp
      rows.push({
        kind: 'day-group',
        dateKey: currentDayKey,
        dateLabel: new Date(firstTs).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        items: nestFlatDayItems(currentFlat),
      })
      currentFlat = []
    }
  }

  for (const event of events) {
    const dayKey = new Date(event.timestamp).toDateString()
    if (dayKey !== currentDayKey) {
      flushGroup()
      currentDayKey = dayKey
    }

    if (event.type === 'fence') {
      currentFlat.push({
        kind: 'milestone',
        fields: {
          id: event.id,
          timestamp: event.timestamp,
          label: event.label,
          reason: event.reason,
          changedBy: event.changedBy,
          actionSlug: event.actionSlug,
        },
      })
      continue
    }

    const { edit } = event
    const eventType = edit.metadata?.event_type
    if (eventType) {
      const versionName =
        typeof edit.metadata?.version_name === 'string' ? edit.metadata.version_name : undefined
      currentFlat.push({
        kind: 'milestone',
        fields: {
          id: edit.id,
          timestamp: edit.timestamp,
          edit,
          label: formatEventType(eventType),
          reason: edit.metadata?.event_reason || versionName,
        },
      })
      continue
    }

    currentFlat.push({ kind: 'edit', edit })
  }
  flushGroup()
  return rows
}

/** Sparse snapshot row from GET /:id/history `versions` (migration 094). */
export interface DocumentVersionRow {
  id: string
  document_id: string
  timestamp: string
  version_name?: string | null
  event_type?: string | null
  reason?: string | null
  created_by?: string | null
  body_length?: number
  body_snapshot?: string
  frontmatter?: Record<string, unknown> | null
}

const VERSION_DEDUPE_WINDOW_MS = 2 * 60 * 1000

/**
 * Merge sparse `document_versions` milestones into the edit list used by the timeline.
 *
 * `document_versions` stores a single `body_snapshot` (after-state only). Real before/after
 * pairs live on matching `document_edits` checkpoint rows (KH verify / request-review).
 * Prefer those edit rows for diffs; only synthesize a version-backed row when there is no
 * nearby same-event edit (e.g. explicit Save version). For those, baseline against the
 * previous version snapshot when available - never invent `content_before: ''`, which made
 * every milestone look like empty -> full document.
 */
export function mergeDocumentVersionsIntoEdits(
  edits: DocumentEditRow[],
  versions: DocumentVersionRow[]
): DocumentEditRow[] {
  if (!versions.length) return edits

  const coveredVersionIds = new Set<string>()

  const enrichedEdits = edits.map((edit) => {
    const eventType = edit.metadata?.event_type
    if (!eventType) return edit
    const editMs = new Date(edit.timestamp).getTime()
    const match = versions.find(
      (v) =>
        v.event_type === eventType &&
        Math.abs(new Date(v.timestamp).getTime() - editMs) <= VERSION_DEDUPE_WINDOW_MS
    )
    if (!match) return edit
    coveredVersionIds.add(match.id)
    const versionName = match.version_name ?? undefined
    const reason = match.reason ?? undefined
    if (!versionName && !reason) return edit
    return {
      ...edit,
      metadata: {
        ...edit.metadata,
        version_name: edit.metadata?.version_name ?? versionName,
        event_reason: edit.metadata?.event_reason ?? reason,
      },
    }
  })

  // ingest_update rows are Wave A safety snapshots (pre-upsert). They are not user-facing
  // "named versions" - surfacing them as "ai (via AI chat) / Ingest Update / pre-upsert
  // snapshot" confused Update Work Spec QA when no document_edits row existed yet.
  const unmatchedVersions = versions.filter(
    (v) => !coveredVersionIds.has(v.id) && v.event_type !== 'ingest_update'
  )
  if (!unmatchedVersions.length) return enrichedEdits

  const versionsOldestFirst = [...versions].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )

  const versionEdits: DocumentEditRow[] = unmatchedVersions.map((v) => {
    const vMs = new Date(v.timestamp).getTime()
    const prev = versionsOldestFirst
      .filter((other) => new Date(other.timestamp).getTime() < vMs)
      .at(-1)
    const contentAfter = v.body_snapshot
    const contentBefore = prev?.body_snapshot
    const role = (v.created_by === 'user' || v.created_by === 'ai' ? v.created_by : 'system') as
      | 'user'
      | 'ai'
      | 'system'
    return {
      id: `version-${v.id}`,
      document_id: v.document_id,
      timestamp: v.timestamp,
      changed_by: role,
      ai_conversation_id: null,
      // created_by is a role ('ai'|'user'), not a display name - do not put it in user_name
      // (that rendered as "ai (via AI chat)").
      user_name: null,
      metadata: {
        event_type: v.event_type ?? undefined,
        event_reason: v.reason ?? undefined,
        channel: 'document-versions',
        version_name: v.version_name ?? undefined,
      },
      change_stats: null,
      previous_length: contentBefore?.length ?? 0,
      new_length: v.body_length ?? (contentAfter?.length ?? 0),
      content_before: contentBefore,
      content_after: contentAfter,
    }
  })

  return [...versionEdits, ...enrichedEdits].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )
}

/** edits must be sorted newest -> oldest (the default order from GET /:id/history). */
export function buildVersionTimeline(edits: DocumentEditRow[]): TimelineRow[] {
  return groupTimelineEvents(edits.map((edit) => ({ type: 'edit', timestamp: edit.timestamp, edit })))
}

/**
 * Merges Knowledge Hub `review-changelog` fence entries into the same timeline as
 * additional milestone rows, so a document with both `document_edits` history and a
 * review-changelog fence shows ONE combined timeline instead of two redundant panels.
 *
 * `verified` fence entries are dropped only when a same-event `document_edits`
 * checkpoint (`metadata.event_type: 'verify'`) is found within
 * `VERIFY_DEDUPE_WINDOW_MS` of the fence entry's timestamp - that checkpoint is what
 * `buildVersionTimeline` already renders as a "Verified" milestone with a usable diff,
 * so keeping the fence entry too would duplicate it. If no matching checkpoint is
 * found (e.g. a hand-authored fence, or data from before checkpoint-writing existed),
 * the fence entry is kept as a fence-only milestone so it's never silently dropped.
 *
 * Other fence actions (`review-requested`, `ai-updated`, `imported-from-guru`, `expired`)
 * do not have a reliably-diffable, uniquely-labeled `document_edits` counterpart today
 * (their checkpoint rows, when present, only capture the changelog-fence text being
 * appended, not the underlying edit - see knowledge-hub-service.js `requestReview`), so
 * they are always rendered as fence-only milestones with no diff action.
 */
export function buildVersionTimelineWithChangelog(
  edits: DocumentEditRow[],
  fenceEntries: ReviewChangelogEntry[]
): TimelineRow[] {
  const editEvents: TimelineEvent[] = edits.map((edit) => ({ type: 'edit', timestamp: edit.timestamp, edit }))

  const verifyCheckpointTimestamps = edits
    .filter((edit) => edit.metadata?.event_type === 'verify')
    .map((edit) => new Date(edit.timestamp).getTime())

  const hasMatchingVerifyCheckpoint = (fenceAtMs: number): boolean =>
    verifyCheckpointTimestamps.some((ts) => Math.abs(ts - fenceAtMs) <= VERIFY_DEDUPE_WINDOW_MS)

  const fenceEvents: TimelineEvent[] = fenceEntries
    .filter((entry) => {
      if (!FENCE_ACTIONS_COVERED_BY_DOCUMENT_EDITS.has(entry.action)) return true
      return !hasMatchingVerifyCheckpoint(new Date(entry.at).getTime())
    })
    .map((entry, idx) => ({
      type: 'fence' as const,
      timestamp: entry.at,
      id: `fence-${entry.at}-${idx}`,
      label: formatFenceAction(entry.action),
      reason: entry.note,
      changedBy: entry.by,
      actionSlug: entry.action,
    }))

  const merged = [...editEvents, ...fenceEvents].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )
  return groupTimelineEvents(merged)
}

/** Returns the badge color classes for a version parent / milestone, whether fence-backed (actionSlug) or checkpoint-backed (edit.metadata.event_type). */
export function milestoneBadgeColor(
  item: TimelineMilestoneFields | TimelineVersionParent
): string | undefined {
  if (item.actionSlug) return FENCE_ACTION_BADGE_COLORS[item.actionSlug]
  const eventType = item.edit?.metadata?.event_type
  return eventType ? EVENT_TYPE_BADGE_COLORS[eventType] : undefined
}

/** Cumulative diff for a selected range of edits (any subset, order-independent). */
export function cumulativeDiffForSelection(
  selected: DocumentEditRow[]
): { before: string; after: string } | null {
  if (selected.length === 0) return null
  const sorted = [...selected].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )
  const oldest = sorted[0]
  const newest = sorted[sorted.length - 1]
  if (oldest.content_before == null || newest.content_after == null) return null
  return { before: oldest.content_before, after: newest.content_after }
}

/** Returns a display label for a changed_by role (user, ai, system). */
export function formatChangedBy(changedBy: string): string {
  if (changedBy === 'ai') return 'AI'
  if (changedBy === 'user') return 'User'
  if (changedBy === 'system') return 'System'
  return changedBy.charAt(0).toUpperCase() + changedBy.slice(1)
}

/**
 * Returns the most accurate attribution label available for an edit row today.
 *
 * `document_edits.user_id` (migration 084, broadened by 085) captures the authenticated
 * session active at write time regardless of `changed_by` - so an AI edit made while
 * "Rowan" was driving the chat now resolves to a real name via the `users` join in
 * GET /:id/history, not just a generic "AI" bucket:
 *
 * - AI edit + resolved user -> "Rowan (via AI chat)" - someone was logged in and directing it.
 * - AI edit + `ai_conversation_id` but no resolved user -> "AI (via chat)" - a live chat
 *   session drove it, but the human identity didn't resolve (e.g. local dev bypass).
 * - AI edit with neither -> "AI (automated)" - a background/scheduled process
 *   (ingestion, expiry scanner, etc.) with no human session at all.
 * - User edit + resolved user -> the real name.
 * - User edit with no resolved user but a raw Knowledge Hub actor string in metadata
 *   (email/flow marker that didn't match an account) -> that raw string, e.g. an email.
 * - System edit with a raw actor string (e.g. a flow marker) -> that raw string.
 * - Anything else -> the coarse role label ("User" / "AI" / "System").
 */
function editChannel(edit: DocumentEditRow): string | null {
  const channel = typeof edit.metadata?.channel === 'string' ? edit.metadata.channel : null
  if (channel) return channel
  const via = typeof edit.metadata?.via === 'string' ? edit.metadata.via : null
  if (via) return via
  if (edit.metadata?.source === 'chat-stream') return 'webapp-chat'
  return null
}

function aiViaLabel(edit: DocumentEditRow): string {
  const channel = editChannel(edit)
  const platform = typeof edit.metadata?.platform === 'string' ? edit.metadata.platform : null
  const client = typeof edit.metadata?.client === 'string' ? edit.metadata.client : null
  if (channel === 'mcp') {
    if (platform === 'cursor' || (client && /cursor/i.test(client))) return 'via Cursor MCP'
    if (platform === 'claude-code' || (client && /claude/i.test(client))) return 'via Claude MCP'
    return 'via MCP'
  }
  if (channel === 'ingestion' || channel === 'drive-import') {
    return channel === 'drive-import' ? 'via Drive import' : 'via Update Work Spec'
  }
  if (channel === 'document-versions' && edit.metadata?.event_type === 'ingest_update') {
    return 'via Update Work Spec'
  }
  if (channel === 'webapp-chat' || edit.metadata?.source === 'chat-stream') return 'via AI chat'
  if (channel === 'agent-ingest') return 'via agent ingest'
  return 'via AI chat'
}

export function formatEditAttribution(edit: DocumentEditRow): string {
  const actorLabel = typeof edit.metadata?.actor === 'string' ? edit.metadata.actor : null

  if (edit.changed_by === 'ai') {
    const via = aiViaLabel(edit)
    if (edit.user_name) return `${edit.user_name} (${via})`
    if (edit.ai_conversation_id || editChannel(edit) === 'webapp-chat') {
      return via === 'via AI chat' ? 'AI (via chat)' : `AI (${via.replace(/^via /, '')})`
    }
    if (editChannel(edit) === 'mcp') return `AI (${via.replace(/^via /, '')})`
    return actorLabel ? `AI (${actorLabel})` : 'AI (automated)'
  }
  if (edit.changed_by === 'user') {
    if (edit.user_name) return edit.user_name
    if (actorLabel) return actorLabel
    return formatChangedBy(edit.changed_by)
  }
  if (edit.changed_by === 'system' && actorLabel) return actorLabel
  return formatChangedBy(edit.changed_by)
}

/** Human-readable labels for the `metadata.saveTrigger` values written by the various save paths (see crud.js, chat-stream/edits.js, document-content.tsx). */
const SAVE_TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual save',
  autosave: 'Autosave',
  restore: 'Restored version',
  undo: 'Undo',
  delete: 'Deleted',
}

/**
 * Returns a short label describing how this edit was made (manual save vs autosave vs
 * a chat-tool edit vs a restore/undo), when that signal is available. Returns null for
 * legacy rows with no `saveTrigger`/`source` metadata so the UI can omit the label
 * rather than show something misleading.
 */
export function formatEditSourceLabel(edit: DocumentEditRow): string | null {
  const channel = editChannel(edit)
  const model = typeof edit.metadata?.model === 'string' ? edit.metadata.model : null
  const client = typeof edit.metadata?.client === 'string' ? edit.metadata.client : null
  const agentName = typeof edit.metadata?.agent_name === 'string' ? edit.metadata.agent_name : null
  const agentKind = typeof edit.metadata?.agent_kind === 'string' ? edit.metadata.agent_kind : null
  const skillSlug = typeof edit.metadata?.skill_slug === 'string' ? edit.metadata.skill_slug : null

  if (channel === 'vsix-sync') return 'Cursor sync'
  if (channel === 'mcp') {
    const parts = ['MCP']
    if (client) parts.push(client)
    if (agentName) parts.push(agentName)
    if (agentKind) parts.push(agentKind)
    if (skillSlug) parts.push(skillSlug)
    if (parts.length === 1 && model) parts.push(model)
    return parts.join(' · ')
  }
  if (channel === 'webapp-chat' || edit.metadata?.source === 'chat-stream') {
    return model ? `Chat edit · ${model}` : 'Chat edit'
  }

  const saveTrigger = typeof edit.metadata?.saveTrigger === 'string' ? edit.metadata.saveTrigger : null
  if (saveTrigger && SAVE_TRIGGER_LABELS[saveTrigger]) return SAVE_TRIGGER_LABELS[saveTrigger]

  if (edit.changed_by === 'ai') {
    return edit.metadata?.source === 'chat-stream' ? 'Chat edit' : null
  }
  return null
}

/** Formats a timestamp for use in a revision dialog heading, e.g. "Jul 8, 2026 at 3:46 PM". */
export function formatRevisionTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  const dateLabel = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const timeLabel = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${dateLabel} at ${timeLabel}`
}

/** Builds the DiffPreviewDialog heading for a single-revision view. */
export function formatSingleRevisionTitle(edit: DocumentEditRow): string {
  return `Revision from ${formatRevisionTimestamp(edit.timestamp)}`
}

/** Builds the DiffPreviewDialog heading for a cumulative (multi-revision) diff view. */
export function formatCumulativeRevisionTitle(selected: DocumentEditRow[]): string {
  if (selected.length === 0) return 'Compare revisions'
  const sorted = [...selected].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  const oldest = formatRevisionTimestamp(sorted[0].timestamp)
  const newest = formatRevisionTimestamp(sorted[sorted.length - 1].timestamp)
  return `Comparing ${selected.length} revisions (${oldest} to ${newest})`
}

/** Formats lines/chars changed summary for a single edit row. */
export function formatChangeSummary(edit: DocumentEditRow): string {
  const stats = edit.change_stats
  const charDelta =
    stats?.added_chars != null ? stats.added_chars : edit.new_length - edit.previous_length
  const parts: string[] = []

  if (stats?.added_lines != null) {
    const sign = stats.added_lines >= 0 ? '+' : ''
    parts.push(`${sign}${stats.added_lines} lines`)
  }

  if (charDelta !== 0 || parts.length === 0) {
    const charSign = charDelta > 0 ? '+' : ''
    parts.push(`${charSign}${charDelta} chars`)
  }

  return parts.join(', ')
}

/** Returns true when both content_before and content_after are available for diffing. */
export function editHasDiffContent(edit: DocumentEditRow): boolean {
  return edit.content_before != null && edit.content_after != null
}
